import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowUpRight, ArrowDownRight, ChevronDown, ChevronUp } from 'lucide-react';
import { truncateAddress, formatBalance } from '../utils/chartHelpers';
import type { DelegationHistoryResponse } from '../types';
import { getDelegationsForSubnet } from '../services/delegationService';

interface DelegationEventsProps {
  coldkey: string;
  hotkey: string;
  netuid: number;
  block_start?: number;
  isExpanded: boolean;
  onToggle: () => void;
  onCostBasisCalculated?: (costBasis: {
    totalTaoSpent: number;
    totalTaoReceived: number;
    totalAlphaReceived: number;
    totalAlphaSpent: number;
    pnl: number;
    pnlPercentage: number;
    currentTaoValue: number;
  }) => void;
  currentTaoValue?: number;
}

export function DelegationEvents({ 
  coldkey, 
  hotkey, 
  netuid, 
  block_start = 0, 
  isExpanded, 
  onToggle,
  onCostBasisCalculated,
  currentTaoValue = 0
}: DelegationEventsProps) {
  const [delegations, setDelegations] = useState<DelegationHistoryResponse['data']>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastCalculatedValue, setLastCalculatedValue] = useState(0);

  // Memoize the cost basis calculation to avoid recalculating on every render
  const calculateCostBasis = useCallback((delegationData: DelegationHistoryResponse['data']) => {
    return delegationData.reduce((acc, delegation) => {
      const taoAmount = Number(BigInt(delegation.amount)) / 1e9;
      const alphaAmount = Number(BigInt(delegation.alpha)) / 1e9;

      if (delegation.action === 'DELEGATE') {
        acc.totalTaoSpent += taoAmount;
        acc.totalAlphaReceived += alphaAmount;
      } else {
        acc.totalTaoReceived += taoAmount;
        acc.totalAlphaSpent += alphaAmount;
      }
      return acc;
    }, {
      totalTaoSpent: 0,
      totalTaoReceived: 0,
      totalAlphaReceived: 0,
      totalAlphaSpent: 0,
      pnl: 0,
      pnlPercentage: 0,
      currentTaoValue: currentTaoValue
    });
  }, []);

  // Memoize the PnL calculation
  const calculatePnL = useCallback((costBasis: any) => {
    // Net TAO invested = TAO spent - TAO received
    const netTaoInvested = costBasis.totalTaoSpent - costBasis.totalTaoReceived;
    
    // Current value + TAO already received - Total TAO spent
    costBasis.pnl = (currentTaoValue + costBasis.totalTaoReceived) - costBasis.totalTaoSpent;
    
    // Calculate percentage return
    if (netTaoInvested > 0) {
      costBasis.pnlPercentage = (costBasis.pnl / netTaoInvested) * 100;
    }
    
    return costBasis;
  }, [currentTaoValue]);

  // Fetch delegations only when expanded and not already loaded
  useEffect(() => {
    const fetchDelegations = async () => {
      // Only fetch when expanded and we have a valid coldkey and delegations aren't already loaded
      if (!isExpanded || !coldkey || (delegations.length > 0 && !loading)) return;
      
      setLoading(true);
      setError(null);

      try {
        console.log(`Fetching delegations for wallet ${coldkey} on subnet ${netuid}`);
        
        // Get delegations from API or Firebase
        const delegationData = await getDelegationsForSubnet(coldkey, netuid);
        
        console.log(`Received ${delegationData.length} delegations for subnet ${netuid}`);
        
        // Filter delegations by block_start if provided
        let filteredDelegations = delegationData;
        if (block_start > 0) {
          console.log(`Filtering delegations after block ${block_start} for subnet ${netuid}`);
          filteredDelegations = filteredDelegations.filter(
            (d: any) => d.block_number >= block_start
          );
        }

        // Sort delegations by block number in descending order
        const sortedDelegations = filteredDelegations.sort(
          (a: any, b: any) => b.block_number - a.block_number
        );

        setDelegations(sortedDelegations);

        // Calculate cost basis and PnL
        if (sortedDelegations.length > 0) {
          const costBasis = calculateCostBasis(sortedDelegations);
          const costBasisWithPnL = calculatePnL(costBasis);
          
          // Only call the callback if the value has changed significantly
          if (Math.abs(costBasisWithPnL.pnl - lastCalculatedValue) > 0.0001) {
            setLastCalculatedValue(costBasisWithPnL.pnl);
            onCostBasisCalculated?.(costBasisWithPnL);
          }
        }
      } catch (err) {
        console.error('Failed to fetch delegations:', err);
        setError('Failed to load delegation history');
      } finally {
        setLoading(false);
      }
    };

    fetchDelegations();
  }, [coldkey, netuid, block_start, isExpanded, calculateCostBasis, calculatePnL, onCostBasisCalculated, lastCalculatedValue, delegations.length, loading]);

  // Memoize the summary calculations to avoid recalculating on every render
  const summary = useMemo(() => {
    if (delegations.length === 0) return null;
    
    const totalTaoSpent = delegations.reduce((sum, d) => 
      d.action === 'DELEGATE' ? sum + Number(BigInt(d.amount)) / 1e9 : sum, 0);
    
    const totalTaoReceived = delegations.reduce((sum, d) => 
      d.action === 'UNDELEGATE' ? sum + Number(BigInt(d.amount)) / 1e9 : sum, 0);
    
    const totalAlphaReceived = delegations.reduce((sum, d) => 
      d.action === 'DELEGATE' ? sum + Number(BigInt(d.alpha)) / 1e9 : sum, 0);
    
    const totalAlphaSpent = delegations.reduce((sum, d) => 
      d.action === 'UNDELEGATE' ? sum + Number(BigInt(d.alpha)) / 1e9 : sum, 0);
    
    const netInvestment = totalTaoSpent - totalTaoReceived;
    const profit = (currentTaoValue + totalTaoReceived) - totalTaoSpent;
    const roi = netInvestment > 0 ? ((profit / netInvestment) * 100) : null;
    
    return {
      totalTaoSpent,
      totalTaoReceived,
      totalAlphaReceived,
      totalAlphaSpent,
      netInvestment,
      profit,
      roi
    };
  }, [delegations, currentTaoValue]);

  if (!isExpanded) {
    return (
      <button 
        onClick={onToggle}
        className="w-full mt-4 flex items-center justify-center space-x-2 text-gray-400 hover:text-white transition-colors"
      >
        <span className="text-sm">Delegation History</span>
        <ChevronDown className="w-4 h-4" />
      </button>
    );
  }

  return (
    <div className="space-y-4">
      <button 
        onClick={onToggle}
        className="w-full mt-4 flex items-center justify-center space-x-2 text-gray-400 hover:text-white transition-colors"
      >
        <span className="text-sm">Delegation History</span>
        <ChevronUp className="w-4 h-4" />
      </button>

      <div className="border-t border-white/10 mt-4 pt-4 space-y-4">
        {loading ? (
          <div className="text-center py-4 text-gray-400">Loading delegation history...</div>
        ) : error ? (
          <div className="text-center py-4 text-red-400">{error}</div>
        ) : delegations.length === 0 ? (
          <div className="text-center py-4 text-gray-400">No delegation history found</div>
        ) : (
          <div className="space-y-4">
            {summary && (
              <div className="bg-black/30 backdrop-blur-sm rounded-lg p-4 border border-white/5">
                <h3 className="text-sm font-medium mb-2">Delegation Summary</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-400">Total TAO Spent</p>
                    <p className="text-sm font-medium">{summary.totalTaoSpent.toFixed(4)} τ</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Total TAO Received</p>
                    <p className="text-sm font-medium">{summary.totalTaoReceived.toFixed(4)} τ</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Total Alpha Received</p>
                    <p className="text-sm font-medium">{summary.totalAlphaReceived.toFixed(4)} α</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Total Alpha Spent</p>
                    <p className="text-sm font-medium">{summary.totalAlphaSpent.toFixed(4)} α</p>
                  </div>
                </div>
                
                {/* PnL Information */}
                <div className="mt-3 pt-3 border-t border-white/10">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-400">Net Investment</p>
                      <p className="text-sm font-medium">
                        {summary.netInvestment.toFixed(4)} τ
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Current Value</p>
                      <p className="text-sm font-medium">{currentTaoValue.toFixed(4)} τ</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Profit/Loss</p>
                      <p className={`text-sm font-medium ${
                        summary.profit >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {summary.profit.toFixed(4)} τ
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">ROI</p>
                      <p className={`text-sm font-medium ${
                        summary.profit >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {summary.roi !== null ? `${summary.roi.toFixed(2)}%` : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Only render the first 10 delegations initially for better performance */}
            {delegations.slice(0, 10).map((delegation, index) => {
              const uniqueKey = `${delegation.block_number}-${delegation.timestamp}-${index}`;
              const isDelegate = delegation.action === 'DELEGATE';
              
              return (
                <div 
                  key={uniqueKey}
                  className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/5"
                >
                  <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 md:gap-0">
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        {isDelegate ? (
                          <ArrowUpRight className="w-5 h-5 text-green-400" />
                        ) : (
                          <ArrowDownRight className="w-5 h-5 text-red-400" />
                        )}
                        <span className={`font-semibold ${
                          isDelegate ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {delegation.action}
                        </span>
                      </div>
                      <p className="text-sm text-gray-400">
                        To: {truncateAddress(delegation.delegate?.ss58)}
                      </p>
                      <p className="text-sm text-gray-400">
                        {new Date(delegation.timestamp).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">
                        Block: {delegation.block_number}
                      </p>
                    </div>
                    <div className="text-left md:text-right space-y-1">
                      <p className="text-lg font-bold text-white">
                        {formatBalance(delegation.amount)} τ
                      </p>
                      <p className="text-sm text-gray-400">
                        {formatBalance(delegation.alpha)} α
                      </p>
                      <p className="text-xs text-gray-500">
                        ${parseFloat(delegation.usd).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Show a "Load More" button if there are more than 10 delegations */}
            {delegations.length > 10 && (
              <button
                onClick={() => {/* This would load more delegations */}}
                className="w-full py-2 bg-black/20 hover:bg-black/30 rounded-lg text-gray-400 hover:text-white transition-colors"
              >
                Load More ({delegations.length - 10} remaining)
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}